// src/components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axiosConfig";
import NightModeToggle from "./NightModeToggle";
import SectionIcon from "./SectionIcon";
import { useSectionColors } from "../context/SectionColorContext";
import { useSeasonalTheme } from "../context/SeasonalThemeContext";
import hexToRgba from "../utils/hexToRgba";
import brightenColor from "../utils/brightenColor";
import { CURRENT_VERSION } from "../data/changelog";
import { allowedUnlessOff } from "../utils/navPermissions";
import "./Navbar.css";

// ── Page path → human-readable name ──────────────────────────────────────────
const PAGE_NAMES = {
  "/":                      "Inicio",
  "/analytics":             "Cartera",
  "/chart-summary":        "Resumen Cuadro",
  "/admin":                 "Admin",
  "/accounts":        "Estados de Cuenta",
  "/reports":              "Reportes & Analítica",
  "/closing":               "Gestión Semanal",
  "/invoicing":           "Facturación",
  "/cost-control":        "Control de Costos",
  "/finances":            "Finanzas",
  "/finances/loans":      "Préstamos Financieros",
  "/finances/loans/list": "Préstamos Financieros · Lista",
  "/inventory":           "Inventario",
  "/inventory/stock":     "Existencias",
  "/inventory/movements": "Movimientos de Inventario",
  "/inventory/orders":    "Órdenes de Compra",
  "/inventory/items":     "Artículos y Proveedores",
  "/ai-stats-chat":         "AI Analytics",
  "/updates":       "Novedades",
  "/office":                "Modo Oficina",
  "/operations":            "Operaciones",
  "/price-board":           "Cuadro de Precios",
  "/clients":               "Clientes",
  "/companies":             "Empresas",
  "/conditions":            "Condiciones",
  "/payment-conditions":    "Condiciones de Pago",
  "/product-billing":       "Productos",
  "/product-types":         "Tipos de Producto",
  "/producers":             "Productores",
  "/brands":                "Marcas",
  "/payment-calculator":    "Pagos Pendientes",
  "/purchase-chart":        "Compra Fruta de Productores",
  "/cost-chart":            "Costo Unitario",
  "/cost-chart/list":       "Costo Unitario · Guardados",
  "/local-billing":     "Liquidaciones Productores",
  "/local-billing/list": "Liquidaciones Productores · Guardadas",
  "/purchase-chart/list":   "Compra Fruta de Productores · Guardados",
  "/price-chart":           "Precios de Venta",
  "/price-chart/list":      "Precios de Venta · Guardados",
  "/billing/list":          "Facturas",
  "/billing":               "Facturador Comercial",
  "/return":                "Retornos",
  "/trucks":                "Camiones",
  "/comissions":            "Comisiones",
  "/stowage":               "Estiba",
  "/palletizing":           "Paletizado",
  "/evaluations":           "Evaluaciones",
  "/containers":            "Contenedores",
  "/supplier":              "Gestión de Proveedores",
  "/cost-choice":           "Elección de Costos",
  "/shipment":              "Gestión de Embarques",
  "/shipments":             "Embarques",
  "/tracking":              "Rastreo",
  "/users":                 "Usuarios",
  "/whatsapp-groups":       "Grupos de WhatsApp",
  "/profile":               "Perfil",
  "/settings":              "Configuración",
};

export function getPageName(path) {
  if (!path) return "Unknown";
  // exact match first
  if (PAGE_NAMES[path]) return PAGE_NAMES[path];
  // prefix match for dynamic routes
  const key = Object.keys(PAGE_NAMES).find(k => k !== "/" && path.startsWith(k));
  return key ? PAGE_NAMES[key] : path.replace(/^\//, "").replace(/-/g, " ") || "Home";
}

function timeAgo(isoString) {
  if (!isoString) return "unknown";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60)  return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function OnlineUserCard({ u, picUrl, name, initial, rect }) {
  const borderColor = u.avatar_border_color || "#6366f1";
  const connectedFor = u.connected_at
    ? (() => {
        const secs = Math.floor((Date.now() - new Date(u.connected_at).getTime()) / 1000);
        if (secs < 60)   return `${secs}s`;
        if (secs < 3600) return `${Math.floor(secs / 60)}m`;
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
      })()
    : null;

  const CARD_WIDTH = 240;
  const GAP = 10;
  // Flip to the left of the row instead of the right when there isn't room
  // (e.g. the bubble popover opened near the left edge of the screen, or the
  // sidebar is docked right) — otherwise the card runs off the viewport.
  const overflowsRight = rect && rect.right + GAP + CARD_WIDTH > window.innerWidth;
  const left = rect
    ? (overflowsRight ? Math.max(8, rect.left - CARD_WIDTH - GAP) : rect.right + GAP)
    : 0;
  const style = {
    position: "fixed",
    left,
    // align card's bottom with the hovered row's bottom, clamped on-screen
    top: rect ? Math.max(8, Math.min(rect.bottom - 260, window.innerHeight - 270)) : 0,
    width: CARD_WIDTH,
    zIndex: 99999,
  };

  return ReactDOM.createPortal(
    <div className="sb-hover-card" style={style}>
      {/* Banner — gif or custom color or default accent gradient */}
      <div className="sb-hover-banner" style={
        u.card_gif_url
          ? { backgroundImage: `url(${u.card_gif_url})` }
          : u.card_bg_color
            ? { background: `linear-gradient(135deg, ${u.card_bg_color}cc 0%, ${u.card_bg_color}66 100%)` }
            : { background: `linear-gradient(135deg, ${borderColor}40, ${borderColor}15)` }
      }>
        <div className="sb-hover-banner-overlay" />
      </div>

      {/* Avatar */}
      <div className="sb-hover-avatar-wrap">
        <div className="sb-hover-avatar" style={{ border: `3px solid ${borderColor}`, boxShadow: `0 0 0 2px var(--bg-card)` }}>
          {picUrl ? (
            <img src={picUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
          ) : (
            <span style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{initial}</span>
          )}
        </div>
        <span className="sb-hover-online-dot" />
      </div>

      {/* Info */}
      <div className="sb-hover-body">
        <div className="sb-hover-name">
          {u.name_emoji && <span style={{ marginRight: 5 }}>{u.name_emoji}</span>}
          {name}
        </div>
        {u.username && <div className="sb-hover-username">@{u.username}</div>}

        <div className="sb-hover-meta">
          {u.current_page && (
            <div className="sb-hover-meta-row">
              <span className="sb-hover-meta-icon">📍</span>
              <span>{getPageName(u.current_page)}</span>
            </div>
          )}
          {connectedFor && (
            <div className="sb-hover-meta-row">
              <span className="sb-hover-meta-icon">⏱</span>
              <span>Connected {connectedFor}</span>
            </div>
          )}
          {u.last_active && (
            <div className="sb-hover-meta-row">
              <span className="sb-hover-meta-icon">🕐</span>
              <span>Active {timeAgo(u.last_active)}</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Separate component so it can own its own error state cleanly
function UserAvatar({ src, initial, borderColor, bgColor }) {
  const [error, setError] = useState(false);
  return (
    <div
      className="sb-user-avatar"
      style={{
        border: `2.5px solid ${borderColor}`,
        background: src && !error
          ? undefined
          : `linear-gradient(135deg, ${bgColor || '#6366f1'}, ${bgColor || '#6366f1'}cc)`,
      }}
    >
      {src && !error ? (
        <img
          src={src}
          alt={initial}
          className="sb-avatar-img"
          onError={() => setError(true)}
        />
      ) : (
        initial
      )}
    </div>
  );
}

export default function Navbar({ collapsed, onToggleCollapse, dockSide = "left", onSetDockSide }) {
  const { activeTheme } = useSeasonalTheme();
  const navLogoLight = activeTheme?.navbar_logo_light || "/icons/fruit_1.png";
  const navLogoDark = activeTheme?.navbar_logo_dark || "/icons/fruit_4.png";
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const holdTimerRef = useRef(null);
  const holdFiredRef = useRef(false);
  const HOLD_MS = 550;

  // Cycles left → right → top → bottom → bubble → left on each hold.
  const DOCK_CYCLE = ["left", "right", "top", "bottom", "bubble"];
  const DOCK_LABELS = { left: "izquierda", right: "derecha", top: "arriba", bottom: "abajo", bubble: "burbuja" };
  const startHold = () => {
    holdFiredRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      holdFiredRef.current = true;
      setHolding(false);
      const next = DOCK_CYCLE[(DOCK_CYCLE.indexOf(dockSide) + 1) % DOCK_CYCLE.length];
      onSetDockSide?.(next);
      if (navigator.vibrate) navigator.vibrate(15);
    }, HOLD_MS);
    setHolding(true);
  };
  const cancelHold = () => {
    clearTimeout(holdTimerRef.current);
    setHolding(false);
  };
  const handleLogoClick = (isMobile) => {
    if (holdFiredRef.current) { holdFiredRef.current = false; return; } // hold already switched sides — ignore the click
    if (isMobile) setMobileOpen(false);
    else onToggleCollapse();
  };
  // ── Bubble mode: a draggable floating button that opens the full nav as a
  // popover, instead of a docked bar. Position persists across sessions. ──
  const BUBBLE_SIZE = 56;
  const [bubblePos, setBubblePos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sbBubblePos"));
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved;
    } catch { /* ignore malformed storage */ }
    return { x: window.innerWidth - BUBBLE_SIZE - 20, y: window.innerHeight - BUBBLE_SIZE - 20 };
  });
  const [bubbleOpen, setBubbleOpen] = useState(false);
  // the popover stays mounted a moment after closing so it can animate out
  const [bubbleMounted, setBubbleMounted] = useState(false);
  useEffect(() => {
    if (bubbleOpen) { setBubbleMounted(true); return undefined; }
    const t = setTimeout(() => setBubbleMounted(false), 260);
    return () => clearTimeout(t);
  }, [bubbleOpen]);
  const [bubbleDragging, setBubbleDragging] = useState(false); // just for visual feedback (scale/shadow) while dragging
  const bubbleDragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const BUBBLE_DRAG_THRESHOLD = 2; // small — a light touch should start moving it right away

  const clampBubble = (x, y) => ({
    x: Math.min(Math.max(x, 8), window.innerWidth - BUBBLE_SIZE - 8),
    y: Math.min(Math.max(y, 8), window.innerHeight - BUBBLE_SIZE - 8),
  });

  const handleBubblePointerDown = (e) => {
    if (dockSide !== "bubble") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    bubbleDragRef.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, origX: bubblePos.x, origY: bubblePos.y };
    startHold();                         // held still for HOLD_MS: back to the docked bar (a drag cancels it)
  };
  const handleBubblePointerMove = (e) => {
    const d = bubbleDragRef.current;
    if (!d.dragging) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > BUBBLE_DRAG_THRESHOLD || Math.abs(dy) > BUBBLE_DRAG_THRESHOLD)) {
      d.moved = true;
      cancelHold();
      setBubbleDragging(true);
    }
    if (d.moved) setBubblePos(clampBubble(d.origX + dx, d.origY + dy));
  };
  const handleBubblePointerUp = () => {
    const d = bubbleDragRef.current;
    if (!d.dragging) return;
    cancelHold();
    if (holdFiredRef.current) {           // the hold already switched the menu to the bar
      holdFiredRef.current = false;
      bubbleDragRef.current.dragging = false;
      setBubbleOpen(false);
      return;
    }
    if (d.moved) {
      setBubblePos(pos => {
        localStorage.setItem("sbBubblePos", JSON.stringify(pos));
        return pos;
      });
    } else {
      setBubbleOpen(o => !o); // no drag happened — treat as a click
    }
    bubbleDragRef.current.dragging = false;
    setBubbleDragging(false);
  };
  // Keep the bubble on-screen if the window is resized/rotated.
  useEffect(() => {
    if (dockSide !== "bubble") return;
    const onResize = () => setBubblePos(pos => clampBubble(pos.x, pos.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [dockSide]);

  // Anchor the popover on whichever side/corner keeps it fully on-screen.
  const bubbleOpensLeft = bubblePos.x > window.innerWidth / 2;
  const bubbleOpensUp   = bubblePos.y > window.innerHeight / 2;
  // The panel opens to the SIDE of the bubble (left/right), so it only needs
  // a horizontal gap. Vertically it shares an edge with the bubble (top-to-top
  // or bottom-to-bottom) — no extra offset there, or the panel's own header
  // (its collapse-toggle button, right at the panel's top) ends up sitting on
  // top of the bubble's screen position, silently stealing its taps.
  const bubblePanelStyle = {
    ...(bubbleOpensLeft
      ? { right: window.innerWidth - bubblePos.x + 12 }
      : { left: bubblePos.x + BUBBLE_SIZE + 12 }),
    ...(bubbleOpensUp
      ? { bottom: window.innerHeight - bubblePos.y - BUBBLE_SIZE }
      : { top: bubblePos.y }),
  };

  const [footerOpen, setFooterOpen] = useState(false);
  const [onlineUsers, setOnlineUsers]   = useState([]);
  const [hoveredUser, setHoveredUser]   = useState(null); // { id, rect }
  // null = not yet confirmed by server (banner hidden until API responds)
  const [dbMode, setDbMode] = useState(null);
  useEffect(() => {
    api.get('settings/database/').then(r => {
      const active = r.data.active || 'default';
      setDbMode(active);
      localStorage.setItem('pf-db-mode', active);
    }).catch(() => {
      // API unreachable — default to production so the banner never shows incorrectly
      setDbMode('default');
    });
  }, []);
  const heartbeatRef = useRef(null);
  const onlineRef    = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { colors: sectionColors } = useSectionColors();

  // "Novedades" alert: shown until this user opens the page after a new changelog version (remembered per browser/user)
  const novedadesKey = `novedades_seen_${user?.id ?? "anon"}`;
  const readSeen = () => { try { return localStorage.getItem(novedadesKey); } catch { return null; } };
  const [novedadesSeen, setNovedadesSeen] = useState(readSeen);
  useEffect(() => { setNovedadesSeen(readSeen()); }, [novedadesKey]);                       // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.pathname !== "/updates") return;
    try { localStorage.setItem(novedadesKey, CURRENT_VERSION); } catch { /* storage blocked: the alert just stays */ }
    setNovedadesSeen(CURRENT_VERSION);
  }, [location.pathname, novedadesKey]);
  const novedadesNew = novedadesSeen !== CURRENT_VERSION && location.pathname !== "/updates";
  const hoverStyle = (key) => {
    const color = sectionColors[key];
    if (!color) return undefined;
    const glow = brightenColor(color, 14, 20);
    return {
      "--sb-item-hover": hexToRgba(color, 0.30),
      "--sb-item-hover-border": hexToRgba(glow, 0.65),
      "--sb-item-hover-glow": hexToRgba(glow, 0.45),
    };
  };

  useEffect(() => {
    setOpenMenu(null);
    setOpenSubMenu(null);
    setMobileOpen(false);
    setBubbleOpen(false);
  }, [location]);

  // ── Heartbeat — keep current user marked as online, sync emoji/page/gif ──
  useEffect(() => {
    if (!user) return;
    const beat = () => {
      const emoji      = localStorage.getItem(`pf-name-emoji-${user.id || 'user'}`) || '';
      const cardGif    = localStorage.getItem(`pf-card-gif-${user.id || 'user'}`) || '';
      const cardColor  = localStorage.getItem(`pf-card-bg-color-${user.id || 'user'}`) || '#6366f1';
      api.post("users/heartbeat/", {
        name_emoji:    emoji,
        current_page:  location.pathname,
        card_gif_url:  cardGif,
        card_bg_color: cardColor,
      }).catch(() => {});
    };
    beat();
    heartbeatRef.current = setInterval(beat, 30_000);
    return () => clearInterval(heartbeatRef.current);
  }, [user, location.pathname]);

  // ── Poll online users every 30 s ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const fetch = () =>
      api.get("users/online/")
        .then(r => setOnlineUsers(r.data || []))
        .catch(() => {});
    fetch();
    onlineRef.current = setInterval(fetch, 30_000);
    return () => clearInterval(onlineRef.current);
  }, [user]);

  // ── Sync DB mode from localStorage (updated by SettingsPage) ─────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'pf-db-mode') setDbMode(e.newValue || 'default');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Push layout down by banner height when test DB is active ─────────────
  useEffect(() => {
    const root = document.documentElement;
    if (dbMode === 'test_db') {
      root.style.setProperty('--test-banner-h', '40px');
    } else {
      root.style.removeProperty('--test-banner-h');
    }
  }, [dbMode]);

  const handleLogout = () => { logout(); navigate("/login"); };

  // In horizontal (top/bottom) mode the dropdowns are floating flyouts, so a
  // click anywhere outside the bar should close them — unlike the vertical
  // sidebar, where they're inline accordions that stay open.
  useEffect(() => {
    const horizontal = dockSide === "top" || dockSide === "bottom";
    if (!horizontal || (openMenu === null && openSubMenu === null && !footerOpen)) return;
    const onDocMouseDown = (e) => {
      if (e.target.closest?.(".sb-root")) return;
      setOpenMenu(null);
      setOpenSubMenu(null);
      setFooterOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [dockSide, openMenu, openSubMenu, footerOpen]);

  if (!user) return null;

  const isSuperuser = !!(
    user.is_superuser === true || user.is_superuser === "true" ||
    user.is_admin === true || user.is_admin === "true" ||
    user.role?.toLowerCase() === "superuser" || user.role?.toLowerCase() === "admin"
  );

  const isStaff = !isSuperuser && !!(
    user.is_staff === true || user.is_staff === "true" ||
    user.role?.toLowerCase() === "staff"
  );

  const userInitial = (user.first_name || user.username || "?").charAt(0).toUpperCase();
  const userDisplayName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;
  const userRole = isSuperuser ? "Superusuario" : isStaff ? "Personal" : "Usuario";

  // Match exactly how ProfilePage.js builds the URL (no base prepend)
  const profilePicUrl = user.profile_picture
    ? `${user.profile_picture}?t=${Date.now()}`
    : null;
  const borderColor = user.avatar_border_color || "#6366f1";

  // Name emoji — stored in localStorage by ProfilePage
  const nameEmoji   = localStorage.getItem(`pf-name-emoji-${user.id || 'user'}`) || '';
  // Avatar background color — stored in localStorage by ProfilePage
  const avatarBgColor = localStorage.getItem(`pf-avatar-bg-${user.id || 'user'}`) || '#6366f1';
  // Card GIF background — stored in localStorage by ProfilePage
  const cardGifUrl  = localStorage.getItem(`pf-card-gif-${user.id || 'user'}`) || '';
  // Card background color fallback — stored in localStorage by ProfilePage
  const cardBgColor = localStorage.getItem(`pf-card-bg-color-${user.id || 'user'}`) || '#6366f1';
  // True when any custom card background (gif or color) is active
  const hasCardBg   = Boolean(cardGifUrl) || cardBgColor !== '#6366f1';

  const isAt = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(path + "/");

  // ── Permission helper ─────────────────────────────────────────────────────
  // Superusers and staff bypass all checks; regular users need explicit keys.
  // 'admin_supplier' also accepts the old per-tab keys for backward compat.
  const can = (key) => {
    if (isSuperuser || isStaff) return true;
    const p = user.nav_permissions || {};
    if (key === 'admin_supplier') {
      return !!(p.admin_supplier || p.admin_supplier_containers ||
                p.admin_supplier_trucks || p.admin_supplier_stowage ||
                p.admin_supplier_palletizing || p.admin_supplier_evaluations ||
                p.admin_supplier_cost_choices);
    }
    return !!p[key];
  };
  const canAny = (...keys) => keys.some(k => can(k));

  // ── Reusable sidebar content ─────────────────────────────────────────────
  const renderContent = (isMobile, isBubblePanel = false) => {
    const isHorizontal = !isMobile && (dockSide === "top" || dockSide === "bottom");
    // The bubble popover always shows full labels — it's a floating menu, not
    // "the collapsed sidebar", so it must not depend on the docked bar's
    // collapsed state at all.
    const show = isMobile || isBubblePanel || (!collapsed && !isHorizontal);
    // Dropdowns still need to render in horizontal mode (as CSS flyouts) even
    // though labels are hidden — only `show` on its own would skip them.
    const showFlyouts = show || isHorizontal;
    const closeIfMobile = isMobile ? () => setMobileOpen(false) : undefined;
    // Inside the popover, the header icon has nothing meaningful to
    // collapse/expand — a tap there just closes the popover instead.
    const handleHeaderClick = () => {
      if (holdFiredRef.current) { holdFiredRef.current = false; return; }
      if (isBubblePanel) { setBubbleOpen(false); return; }
      handleLogoClick(isMobile);
    };

    return (
      <>
        {/* ── Header (not in the bubble popover: the bubble itself is the logo) ── */}
        {!isBubblePanel && <div className="sb-header">
          {/* Logo icon doubles as the expand/collapse toggle on desktop.
              Click-and-hold (or long-press on touch) moves the sidebar to the other side. */}
          <button
            className={`sb-logo-btn${holding ? " sb-holding" : ""}`}
            onClick={handleHeaderClick}
            onMouseDown={isMobile ? undefined : startHold}
            onMouseUp={isMobile ? undefined : cancelHold}
            onMouseLeave={isMobile ? undefined : cancelHold}
            onTouchStart={isMobile ? undefined : startHold}
            onTouchEnd={isMobile ? undefined : cancelHold}
            aria-label={isMobile ? "Cerrar menú" : isBubblePanel ? "Cerrar menú" : collapsed ? "Expandir barra lateral" : "Contraer barra lateral"}
            title={isMobile || isBubblePanel ? undefined : `${collapsed ? "Expandir" : "Contraer"} — mantén presionado para mover la barra (siguiente: ${DOCK_LABELS[DOCK_CYCLE[(DOCK_CYCLE.indexOf(dockSide) + 1) % DOCK_CYCLE.length]]})`}
          >
            {!isMobile && <span className="sb-hold-ring" aria-hidden="true" />}
            <img src={navLogoLight} alt="Mostrar u ocultar barra lateral" className="sb-logo-img sb-light" />
            <img src={navLogoDark} alt="Mostrar u ocultar barra lateral" className="sb-logo-img sb-dark" />
          </button>
        </div>}

        {/* ── Body (recessed nav area) ─────────────────────────────────── */}
        <div className="sb-body">
          <nav className="sb-nav" aria-label="Navegación principal">

            {/* Home */}
            <Link
              to="/"
              className={`sb-item${isAt("/") ? " active" : ""}`}
              style={hoverStyle("nav_home")}
              onClick={closeIfMobile}
            >
              <SectionIcon iconKey="nav_home" defaultEmoji="🏠" className="sb-icon" />
              {show && <span className="sb-label">Inicio</span>}
            </Link>

            {/* ── ⚙️ Admin — one consolidated hub page instead of a long flyout list ── */}
            {canAny('admin_settings','admin_companies','admin_company_bank_accounts','admin_clients','admin_payment_conditions',
                    'admin_conditions','admin_products','admin_product_types','admin_box_types','admin_producers','admin_brands',
                    'admin_shipment','admin_supplier',
                    'admin_users_list','admin_whatsapp_groups') && (
              <Link
                className={`sb-item${
                  isAt("/admin") || isAt("/supplier") || isAt("/settings") || isAt("/companies") ||
                  isAt("/company-bank-accounts") ||
                  isAt("/clients") || isAt("/product-billing") || isAt("/product-types") || isAt("/producers") ||
                  isAt("/brands") || isAt("/shipment") ||
                  isAt("/users") || isAt("/payment-conditions") || isAt("/conditions") ||
                  isAt("/whatsapp-groups")
                    ? " active" : ""
                }`}
                to="/admin"
                style={hoverStyle("nav_admin")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_admin" defaultEmoji="⚙️" className="sb-icon" />
                {show && <span className="sb-label">Admin</span>}
              </Link>
            )}

            {/* ── 👥 Estados de Cuenta ──────────────────────────────── */}
            {canAny('cuentas_statements') && (
              <Link
                className={`sb-item${isAt("/accounts") || isAt("/account-statements") ? " active" : ""}`}
                to="/accounts"
                style={hoverStyle("nav_estados_cuenta")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_estados_cuenta" defaultEmoji="👥" className="sb-icon" />
                {show && <span className="sb-label">Estados de Cuenta</span>}
              </Link>
            )}

            {/* ── 🏦 Finanzas ───────────────────────────────────────── */}
            {canAny('cuentas_return', 'finance_loans', 'finance_loans_lista') && (
              <Link
                className={`sb-item${isAt("/finances") || isAt("/return") ? " active" : ""}`}
                to="/finances"
                style={hoverStyle("nav_finances")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_finances" defaultEmoji="🏦" className="sb-icon" />
                {show && <span className="sb-label">Finanzas</span>}
              </Link>
            )}

            {/* ── 📊 Reportes & Analítica ───────────────────────────── */}
            {canAny('reportes_director','reportes_cartera','reportes_pagos','reportes_resumen_cuadro') && (
              <Link
                className={`sb-item${isAt("/reports") || isAt("/analytics") || isAt("/payment-calculator") || isAt("/chart-summary") ? " active" : ""}`}
                to="/reports"
                style={hoverStyle("nav_reportes")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_reportes" defaultEmoji="📊" className="sb-icon" />
                {show && <span className="sb-label">Reportes & Analítica</span>}
              </Link>
            )}

            {/* ── 🔒 Gestión Semanal ───────────────────────────────────── */}
            {canAny('cierres_precios','cierres_costos','cierres_fact_local','cuentas_price_chart','cierres_precios_lista','cuentas_price_chart_lista','cierres_costos_lista','cierres_fact_local_lista') && (
              <Link
                className={`sb-item${
                  isAt("/closing") || isAt("/purchase-chart") || isAt("/price-chart") ||
                  isAt("/cost-chart") || isAt("/local-billing")
                    ? " active" : ""
                }`}
                to="/closing"
                style={hoverStyle("nav_cierres")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_cierres" defaultEmoji="🔒" className="sb-icon" />
                {show && <span className="sb-label">Gestión Semanal</span>}
              </Link>
            )}

            {/* ── 📑 Facturación ───────────────────────────────────────── */}
            {canAny('cuentas_billing','cuentas_billing_list','packing_list') && (
              <Link
                className={`sb-item${
                  isAt("/invoicing") || isAt("/billing")
                    ? " active" : ""
                }`}
                to="/invoicing"
                style={hoverStyle("nav_facturacion")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_facturacion" defaultEmoji="📑" className="sb-icon" />
                {show && <span className="sb-label">Facturación</span>}
              </Link>
            )}

            {/* ── 🚢 Operaciones ───────────────────────────────────────── */}
            {canAny('embarques_tracking', 'shipments_registro', 'shipments_registro_lista') && (
              <Link
                className={`sb-item${isAt("/operations") || isAt("/tracking") || isAt("/shipments") ? " active" : ""}`}
                to="/operations"
                style={hoverStyle("nav_operaciones")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_operaciones" defaultEmoji="🚢" className="sb-icon" />
                {show && <span className="sb-label">Operaciones</span>}
              </Link>
            )}

            {/* ── 🔌 APIs ──────────────────────────────────────────────── */}
            {canAny('embarques_erp_explorer', 'allio_explorer', 'cuadro_precios') && (
              <Link
                className={`sb-item${isAt("/apis") || isAt("/erp-explorer") || isAt("/allio-explorer") || isAt("/price-board") ? " active" : ""}`}
                to="/apis"
                style={hoverStyle("nav_apis")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_apis" defaultEmoji="🔌" className="sb-icon" />
                {show && <span className="sb-label">APIs</span>}
              </Link>
            )}

            {/* ── 💰 Control de Costos ──────────────────────────────── */}
            {canAny('control_contenedores','control_comisiones','control_camiones',
                    'control_estiba','control_paletizado','control_evaluaciones') && (
              <Link
                className={`sb-item${
                  isAt("/cost-control") || isAt("/containers") || isAt("/comissions") ||
                  isAt("/trucks") || isAt("/stowage") || isAt("/palletizing") || isAt("/evaluations")
                    ? " active" : ""
                }`}
                to="/cost-control"
                style={hoverStyle("nav_control_costos")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_control_costos" defaultEmoji="💰" className="sb-icon" />
                {show && <span className="sb-label">Control de Costos</span>}
              </Link>
            )}

            {/* ── 📦 Inventario ─────────────────────────────────────── */}
            {canAny('inventory_stock','inventory_movements','inventory_orders','inventory_receipts','inventory_items') && (
              <Link
                className={`sb-item${isAt("/inventory") ? " active" : ""}`}
                to="/inventory"
                style={hoverStyle("nav_inventory")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_inventory" defaultEmoji="📦" className="sb-icon" />
                {show && <span className="sb-label">Inventario</span>}
              </Link>
            )}

            {/* ── 🤖 AI Analytics ───────────────────────────────────── */}
            {can('ai_analytics') && (
              <Link
                to="/ai-stats-chat"
                className={`sb-item${isAt("/ai-stats-chat") ? " active" : ""}`}
                style={hoverStyle("nav_ai_analytics")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_ai_analytics" defaultEmoji="🤖" className="sb-icon" />
                {show && <span className="sb-label">AI Analytics</span>}
              </Link>
            )}

            {/* ── 🆕 Novedades ───────────────────────────────────── */}
            {can('actualizaciones') && (
              <Link
                to="/updates"
                className={`sb-item${isAt("/updates") ? " active" : ""}`}
                style={hoverStyle("nav_actualizaciones")}
                onClick={closeIfMobile}
              >
                <span className="sb-icon-wrap">
                  <SectionIcon iconKey="nav_actualizaciones" defaultEmoji="🆕" className="sb-icon" />
                  {novedadesNew && <span className="sb-new-dot" role="status" aria-label="Hay novedades" title="Hay novedades" />}
                </span>
                {show && <span className="sb-label">Novedades</span>}
                {show && novedadesNew && <span className="sb-new-pill">NUEVO</span>}
              </Link>
            )}

            {/* ── 🕹️ Modo Oficina — walkable-office alternate navigation ── */}
            {allowedUnlessOff(user, "modo_oficina") && (
              <Link
                to="/office"
                className={`sb-item${isAt("/office") ? " active" : ""}`}
                style={hoverStyle("nav_office_mode")}
                onClick={closeIfMobile}
              >
                <SectionIcon iconKey="nav_office_mode" defaultEmoji="🕹️" className="sb-icon" />
                {show && <span className="sb-label">Modo Oficina</span>}
              </Link>
            )}

          </nav>

          {/* ── Footer ────────────────────────────────────────────────── */}
          <div className="sb-footer">
            {/* Online users (other sessions active in last 2 min) — also shown
                in the horizontal bar as a compact avatar row (CSS hides names) */}
            {onlineUsers.length > 0 && (show || isHorizontal) && (
              <div className="sb-online-list">
                <div className="sb-online-label">
                  <span className="sb-online-dot" />
                  <span className="sb-online-count">{onlineUsers.length} en línea</span>
                </div>
                {onlineUsers.map(u => {
                  const picUrl = u.profile_picture ? `${u.profile_picture}?t=1` : null;
                  const initial = (u.first_name || u.username || "?").charAt(0).toUpperCase();
                  const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username;
                  return (
                    <div key={u.id} className="sb-online-user"
                      onMouseEnter={e => setHoveredUser({ id: u.id, rect: e.currentTarget.getBoundingClientRect() })}
                      onMouseLeave={() => setHoveredUser(null)}>
                      <div className="sb-online-avatar" style={{ border: `2px solid ${u.avatar_border_color || "#6366f1"}`, background: u.card_bg_color || undefined }}>
                        {picUrl ? (
                          <img src={picUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                        ) : initial}
                      </div>
                      <span className="sb-online-name">
                        {u.name_emoji && <span style={{ marginRight: 3 }}>{u.name_emoji}</span>}
                        {name}
                      </span>
                      {hoveredUser?.id === u.id && (
                        <OnlineUserCard u={u} picUrl={picUrl} name={name} initial={initial} rect={hoveredUser.rect} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* User card — clicking toggles the actions below */}
            <button
              className={`sb-user-card sb-user-card-btn${footerOpen ? " open" : ""}`}
              onClick={() => setFooterOpen(o => !o)}
              aria-expanded={footerOpen}
              aria-label="Mostrar u ocultar menú de perfil"
              style={cardGifUrl ? {
                backgroundImage: `url(${cardGifUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                overflow: 'hidden',
                minHeight: '72px',
              } : cardBgColor !== '#6366f1' ? {
                background: `linear-gradient(135deg, ${cardBgColor}cc 0%, ${cardBgColor}66 100%)`,
                position: 'relative',
              } : undefined}
            >
              {/* Dark overlay when GIF is active so text stays readable */}
              {cardGifUrl && (
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: 'inherit',
                  background: 'rgba(0,0,0,0.48)', pointerEvents: 'none',
                }} />
              )}
              <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 10, flex: 1, overflow: 'hidden' }}>
                <UserAvatar
                  src={profilePicUrl}
                  initial={userInitial}
                  borderColor={borderColor}
                  bgColor={avatarBgColor}
                />
                {show && (
                  <>
                    <div className="sb-user-info">
                      <div className="sb-user-name" style={hasCardBg ? { color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)' } : undefined}>
                        {nameEmoji && <span style={{ marginRight: 4 }}>{nameEmoji}</span>}
                        {userDisplayName}
                      </div>
                      <div className="sb-user-role" style={hasCardBg ? { color: 'rgba(255,255,255,0.75)' } : undefined}>{userRole}</div>
                    </div>
                    <span className="sb-footer-chevron" style={hasCardBg ? { color: 'rgba(255,255,255,0.7)' } : undefined}>›</span>
                  </>
                )}
              </span>
            </button>

            {/* Slide-up drawer — hidden by default */}
            <div className={`sb-footer-links${footerOpen ? " open" : ""}`}>
              <div className="sb-item sb-night-mode-item">
                <NightModeToggle />
              </div>
              <Link
                className="sb-item"
                to="/profile"
                onClick={closeIfMobile}
              >
                <span className="sb-icon">👤</span>
                {showFlyouts && <span className="sb-label">Perfil</span>}
              </Link>
              <button className="sb-item sb-logout-btn" onClick={handleLogout}>
                <span className="sb-icon">🚪</span>
                {showFlyouts && <span className="sb-label">Cerrar sesión</span>}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {/* ── Test DB banner ───────────────────────────────────────────────── */}
      {dbMode === 'test_db' && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          height: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 40%, #f59e0b 100%)',
          boxShadow: '0 2px 12px rgba(245, 158, 11, 0.4)',
        }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>🧪</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1c1007', letterSpacing: '0.2px' }}>
            Estás trabajando en la <strong>base de datos de prueba</strong> — los datos de producción están a salvo
          </span>
          <span style={{ fontSize: 16, lineHeight: 1 }}>🧪</span>
        </div>
      )}

      {/* ── Desktop sidebar (flex child inside .layout-root) ─────────────── */}
      <aside
        className={`sb-root${collapsed ? " sb-collapsed" : ""}${
          dockSide === "top" || dockSide === "bottom" ? ` sb-horizontal sb-dock-${dockSide}` : ""
        }${dockSide === "bubble" ? " sb-bubble-hidden" : ""}`}
        aria-label="Barra lateral"
      >
        {renderContent(false)}
      </aside>

      {/* ── Bubble mode: floating draggable button + popover, instead of a
          docked bar. Click toggles the popover; drag repositions the bubble
          (a small movement threshold tells the two apart). ───────────────── */}
      {dockSide === "bubble" && (
        <>
          {bubbleOpen && (
            <div className="sb-bubble-backdrop" onClick={() => setBubbleOpen(false)} aria-hidden="true" />
          )}
          <button
            className={`sb-bubble-btn${bubbleDragging ? " sb-bubble-dragging" : ""}${!bubbleOpen && !bubbleDragging ? " sb-bubble-pulse" : ""}${bubbleOpen && !bubbleDragging ? " sb-bubble-active" : ""}${holding ? " sb-holding" : ""}`}
            style={{ left: bubblePos.x, top: bubblePos.y, width: BUBBLE_SIZE, height: BUBBLE_SIZE }}
            onPointerDown={handleBubblePointerDown}
            onPointerMove={handleBubblePointerMove}
            onPointerUp={handleBubblePointerUp}
            onPointerCancel={handleBubblePointerUp}
            aria-label="Abrir navegación"
            aria-expanded={bubbleOpen}
            title="Clic para abrir · arrastra para mover · mantén presionado para volver a la barra"
          >
            <span className="sb-hold-ring" aria-hidden="true" />
            <img src={navLogoLight} alt="" className="sb-logo-img sb-light" />
            <img src={navLogoDark} alt="" className="sb-logo-img sb-dark" />
            {onlineUsers.length > 0 && <span className="sb-bubble-online-badge" title={`${onlineUsers.length} en línea`} />}
          </button>
          {bubbleMounted && (
            // opens as a circle growing out of the bubble until it covers the menu, and shrinks back into it on close.
            // --orb-x/--orb-y are the bubble's centre seen from the menu (12px gap beside it; see .sb-bubble-panel-in in Navbar.css).
            <div className={`sb-root sb-bubble-panel ${bubbleOpen ? "sb-bubble-panel-in" : "sb-bubble-panel-out"}`}
              style={{ ...bubblePanelStyle,
                "--orb-x": bubbleOpensLeft ? `calc(100% + ${12 + BUBBLE_SIZE / 2}px)` : `${-(12 + BUBBLE_SIZE / 2)}px`,
                "--orb-y": bubbleOpensUp ? `calc(100% - ${BUBBLE_SIZE / 2}px)` : `${BUBBLE_SIZE / 2}px` }}
              aria-label="Barra lateral" aria-hidden={!bubbleOpen}>
              {renderContent(false, true)}
            </div>
          )}
        </>
      )}

      {/* ── Mobile top bar ───────────────────────────────────────────────── */}
      <div className="sb-mobile-topbar">
        <button
          className="sb-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir navegación"
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>
        <Link to="/" className="sb-mobile-brand">
          <img src={navLogoLight} alt="" className="sb-logo-img sb-light" />
          <img src={navLogoDark} alt="" className="sb-logo-img sb-dark" />
        </Link>
      </div>

      {/* ── Mobile overlay ───────────────────────────────────────────────── */}
      <div
        className={`sb-overlay${mobileOpen ? " open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile drawer ────────────────────────────────────────────────── */}
      <aside
        className={`sb-root sb-mobile-sidebar${mobileOpen ? " open" : ""}`}
        aria-label="Navegación móvil"
        aria-hidden={!mobileOpen}
      >
        {renderContent(true)}
      </aside>
    </>
  );
}
